import React, { useEffect, useMemo, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import { useLazyQuery } from "@apollo/client";

import { withInstallation } from "../../../components/InstallationContext";
import {
  ActionsSection,
  HeadlineSection,
  LeakageAndSegmentsSection,
  OccupancySection,
  RevenueSection,
} from "./OverviewSections";
import { useOverviewStyles } from "./OverviewTab.styles";
import {
  buildOverviewViewModel,
  buildRevenueLeakageViewModel,
  buildRevenueSegmentsViewModel,
  buildOccupancyViewModel,
  getBudgetGoal,
  overviewResponseToData,
} from "./overviewModel";
import { useOverviewBudget } from "./useOverviewBudget";
import {
  insightsRevenueQuery,
  insightsRevenueLeakageQuery,
  insightsRevenueSegmentsQuery,
  insightsOccupancyQuery,
} from "../queries";
import { LoadingError } from "../../../components/LoadingFork";
import { DateTime } from "luxon";

const OVERVIEW_REFETCH_DEBOUNCE_MS = 350;

export function installationDateValue(date = new Date(), timeZoneName) {
  return DateTime
    .fromJSDate(date)
    .setZone(timeZoneName || "Australia/Sydney")
    .toISODate();
}

function OverviewTab({
  period,
  customDays = 30,
  startDate,
  endDate,
  comparisonStartDate,
  comparisonEndDate,
  asOf,
  setTab,
  availableTabs = [],
  facility,
  installation,
  updateFacility,
  onLoadingChange,
}) {
  const classes = useOverviewStyles();
  const budget = useOverviewBudget(facility, updateFacility);
  const [refreshing, setRefreshing] = useState(false);
  const [requestError, setRequestError] = useState(null);
  const [leakageRefreshing, setLeakageRefreshing] = useState(false);
  const [previousLeakageRefreshing, setPreviousLeakageRefreshing] = useState(false);
  const [segmentsRefreshing, setSegmentsRefreshing] = useState(false);
  const [occupancyRefreshing, setOccupancyRefreshing] = useState(false);
  const [leakageResult, setLeakageResult] = useState(null);
  const [previousLeakageResult, setPreviousLeakageResult] = useState(null);
  const [segmentsResult, setSegmentsResult] = useState(null);
  const [occupancyResult, setOccupancyResult] = useState(null);
  const abortControllerRef = useRef(null);
  const hasRequestedRef = useRef(false);
  const [loadRevenue, { data }] = useLazyQuery(insightsRevenueQuery, {
    fetchPolicy: "network-only",
  });
  const [revenueResult, setRevenueResult] = useState(() => data?.insightsRevenue
    ? { facilityId: facility?.id, asOf, value: data.insightsRevenue }
    : null);
  const [loadRevenueLeakage] = useLazyQuery(
    insightsRevenueLeakageQuery,
    { fetchPolicy: "network-only" },
  );
  const [loadPreviousRevenueLeakage] = useLazyQuery(
    insightsRevenueLeakageQuery,
    { fetchPolicy: "network-only" },
  );
  const [loadRevenueSegments] = useLazyQuery(
    insightsRevenueSegmentsQuery,
    { fetchPolicy: "network-only" },
  );
  const [loadOccupancy] = useLazyQuery(
    insightsOccupancyQuery,
    { fetchPolicy: "network-only" },
  );

  const overviewRefreshing = refreshing || leakageRefreshing || previousLeakageRefreshing || segmentsRefreshing || occupancyRefreshing;

  useEffect(() => {
    if (onLoadingChange) onLoadingChange(overviewRefreshing);
    return () => {
      if (onLoadingChange) onLoadingChange(false);
    };
  }, [onLoadingChange, overviewRefreshing]);

  useEffect(() => {
    if (!facility?.id) return undefined;

    if (abortControllerRef.current) abortControllerRef.current.abort();
    setRefreshing(true);
    setLeakageRefreshing(true);
    setPreviousLeakageRefreshing(true);
    setSegmentsRefreshing(true);
    setOccupancyRefreshing(true);
    setRequestError(null);

    const delay = hasRequestedRef.current ? OVERVIEW_REFETCH_DEBOUNCE_MS : 0;
    hasRequestedRef.current = true;
    let controller;
    const timer = setTimeout(() => {
      controller = new AbortController();
      abortControllerRef.current = controller;
      const requestFacilityId = facility.id;
      Promise.resolve(loadRevenue({
        variables: {
          facilityId: facility.id,
          startDate,
          endDate,
          comparisonStartDate,
          comparisonEndDate,
          asOf,
        },
        context: { fetchOptions: { signal: controller.signal } },
      }))
        .then((result) => {
          if (abortControllerRef.current !== controller) return;
          const value = result?.data?.insightsRevenue;
          if (value) {
            setRevenueResult({ facilityId: requestFacilityId, asOf, value });
            Promise.resolve(loadPreviousRevenueLeakage({
              variables: {
                facilityId: requestFacilityId,
                startDate: value.previousPeriod.startDate,
                endDate: value.previousPeriod.endDate,
                asOf,
                comparisonAsOf: asOf !== '23:59',
              },
              context: { fetchOptions: { signal: controller.signal } },
            }))
              .then((previousResult) => {
                if (abortControllerRef.current !== controller) return;
                const previousValue = previousResult?.data?.insightsRevenueLeakage;
                if (previousValue) setPreviousLeakageResult({ facilityId: requestFacilityId, asOf, value: previousValue });
                setPreviousLeakageRefreshing(false);
              })
              .catch((error) => {
                if (controller.signal.aborted) return;
                console.error("Unable to load comparison revenue leakage", error);
                if (abortControllerRef.current === controller) setPreviousLeakageRefreshing(false);
              });
          } else {
            setPreviousLeakageRefreshing(false);
          }
          setRefreshing(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          if (abortControllerRef.current === controller) {
            setRequestError(error);
            setRefreshing(false);
            setPreviousLeakageRefreshing(false);
          }
        });
      Promise.resolve(loadRevenueLeakage({
        variables: { facilityId: facility.id, startDate, endDate, asOf },
        context: { fetchOptions: { signal: controller.signal } },
      }))
        .then((result) => {
          if (abortControllerRef.current !== controller) return;
          const value = result?.data?.insightsRevenueLeakage;
          if (value) setLeakageResult({ facilityId: requestFacilityId, asOf, value });
          setLeakageRefreshing(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error("Unable to load revenue leakage", error);
          if (abortControllerRef.current === controller) setLeakageRefreshing(false);
        });
      Promise.resolve(loadRevenueSegments({
        variables: { facilityId: facility.id, startDate, endDate, asOf },
        context: { fetchOptions: { signal: controller.signal } },
      }))
        .then((result) => {
          if (abortControllerRef.current !== controller) return;
          const value = result?.data?.insightsRevenueSegments;
          if (value) setSegmentsResult({ facilityId: requestFacilityId, asOf, value });
          setSegmentsRefreshing(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error("Unable to load revenue segments", error);
          if (abortControllerRef.current === controller) setSegmentsRefreshing(false);
        });
      Promise.resolve(loadOccupancy({
        variables: {
          facilityId: facility.id,
          startDate,
          endDate,
          comparisonStartDate,
          comparisonEndDate,
          asOf,
        },
        context: { fetchOptions: { signal: controller.signal } },
      }))
        .then((result) => {
          if (abortControllerRef.current !== controller) return;
          const value = result?.data?.insightsOccupancy;
          if (value) setOccupancyResult({ facilityId: requestFacilityId, asOf, value });
          setOccupancyRefreshing(false);
        })
        .catch((error) => {
          if (controller.signal.aborted) return;
          console.error("Unable to load occupancy", error);
          setOccupancyRefreshing(false);
        });
    }, delay);

    return () => {
      clearTimeout(timer);
      if (controller) controller.abort();
    };
  }, [
    comparisonEndDate,
    asOf,
    comparisonStartDate,
    endDate,
    facility?.id,
    loadRevenue,
    loadRevenueLeakage,
    loadPreviousRevenueLeakage,
    loadRevenueSegments,
    loadOccupancy,
    startDate,
  ]);

  const revenue = revenueResult?.facilityId === facility?.id ? revenueResult.value : null;
  const sameCurrentPeriod = (result) => Boolean(
    revenue && result?.facilityId === facility?.id &&
    result.asOf === revenueResult.asOf &&
    result.value.startDate === revenue.currentPeriod.startDate &&
    result.value.endDate === revenue.currentPeriod.endDate
  );
  const revenueLeakage = sameCurrentPeriod(leakageResult) ? leakageResult.value : null;
  const previousRevenueLeakage = revenue &&
    previousLeakageResult?.facilityId === facility?.id &&
    previousLeakageResult.asOf === revenueResult.asOf &&
    previousLeakageResult.value.startDate === revenue.previousPeriod.startDate &&
    previousLeakageResult.value.endDate === revenue.previousPeriod.endDate
    ? previousLeakageResult.value : null;
  const revenueSegments = sameCurrentPeriod(segmentsResult) ? segmentsResult.value : null;
  const occupancy = sameCurrentPeriod(occupancyResult) &&
    occupancyResult.value.previousStartDate === revenue.previousPeriod.startDate &&
    occupancyResult.value.previousEndDate === revenue.previousPeriod.endDate
    ? occupancyResult.value : null;
  // A request that finishes early must not look like a failure while the
  // remaining responses for this overview are still arriving.
  const leakageStatus = overviewRefreshing ? "Loading…" : "Unavailable";
  const previousLeakageStatus = overviewRefreshing ? "Comparison loading…" : "Comparison unavailable";
  const segmentsStatus = overviewRefreshing ? "Loading…" : "Unavailable";
  const occupancyStatus = overviewRefreshing ? "Loading…" : "Unavailable";
  const today = installationDateValue(new Date(), installation?.timeZoneName);
  const includesToday = Boolean(
    revenue &&
    revenue.currentPeriod.startDate <= today &&
    today < revenue.currentPeriod.endDate
  );
  const budgetGoal = getBudgetGoal(
    budget.activeMonthlyRevenue,
    revenue?.currentPeriod?.daily?.length || customDays,
  );

  const viewModel = useMemo(
    () => {
      if (!revenue) return null;
      const overviewData = overviewResponseToData(
        revenue,
        revenueLeakage,
        previousRevenueLeakage,
        occupancy,
      );
      return buildOverviewViewModel(
        overviewData,
        overviewData.compareLabel,
        budgetGoal,
      );
    },
    [revenue, revenueLeakage, previousRevenueLeakage, occupancy, budgetGoal],
  );
  const leakageViewModel = useMemo(
    () => revenueLeakage
      ? buildRevenueLeakageViewModel(
          revenueLeakage,
          budget.activeBuzzOutsPerDay,
          budget.activeFocCardsPerDay,
        )
      : null,
    [
      budget.activeBuzzOutsPerDay,
      budget.activeFocCardsPerDay,
      revenueLeakage,
    ],
  );
  const segmentRows = useMemo(
    () => revenueSegments
      ? buildRevenueSegmentsViewModel(
          revenueSegments,
          revenue?.currentPeriod?.metrics?.hotelNetRevenue || 0,
        )
      : [],
    [revenue, revenueSegments],
  );
  const occupancyViewModel = useMemo(
    () => occupancy ? buildOccupancyViewModel(occupancy) : null,
    [occupancy],
  );

  if (requestError && !viewModel) return <LoadingError error={requestError} />;
  if (!viewModel) return null;

  return (
    <Box className={classes.root}>
      <HeadlineSection
        viewModel={viewModel}
        budgetInputs={budget.inputs}
        onSaveBudget={budget.saveInput}
        includesToday={includesToday}
        leakageStatus={leakageStatus}
        previousLeakageStatus={previousLeakageStatus}
        occupancyStatus={occupancyStatus}
      />
      <ActionsSection
        viewModel={viewModel}
        onNavigate={setTab}
        availableTabs={availableTabs}
        leakageStatus={leakageStatus}
      />
      <RevenueSection
        viewModel={viewModel}
        installation={installation}
        showGateCharges={Boolean(facility?.gates?.length)}
        startDate={revenue.currentPeriod.startDate}
        endDate={revenue.currentPeriod.endDate}
        asOf={revenueResult?.asOf}
      />
      <LeakageAndSegmentsSection
        leakageViewModel={leakageViewModel}
        segmentRows={segmentRows}
        onNavigate={setTab}
        leakageStatus={leakageStatus}
        segmentsStatus={revenueSegments ? null : segmentsStatus}
      />
      <OccupancySection
        occupancyViewModel={occupancyViewModel}
        includesToday={includesToday}
        requestedStartDate={startDate}
        requestedEndDate={endDate}
        status={occupancyStatus}
      />
    </Box>
  );
}

export default withInstallation(OverviewTab);
